import { useEffect, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, BookmarkPlus, X } from "lucide-react";
import { toast } from "sonner";
import { TEMPLATE_CATEGORIES } from "@/lib/allergens";

/**
 * Reusable ingredient statements. Staff save a long ingredient list once and
 * reload it into any product/recipe with one tap — saves re-typing. Templates
 * are site-scoped (backend: /ingredient-templates).
 */
export function IngredientTemplates({ siteId, value, onApply }) {
  const [templates, setTemplates] = useState([]);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("General");

  const load = async () => {
    if (!siteId) return;
    try {
      const { data } = await api.get("/ingredient-templates", withSite(siteId));
      setTemplates(data || []);
    } catch { /* non-critical */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siteId]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name the template"); return; }
    if (!String(value || "").trim()) { toast.error("Nothing to save — type an ingredient list first"); return; }
    try {
      await api.post("/ingredient-templates", { name: name.trim(), text: value, category }, withSite(siteId));
      toast.success("Ingredient template saved");
      setNaming(false); setName(""); setCategory("General");
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/ingredient-templates/${id}`, withSite(siteId));
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-2" data-testid="ingredient-templates">
      <div className="flex flex-wrap items-center gap-2">
        {naming ? (
          <div className="flex flex-wrap items-center gap-2">
            <Input
              data-testid="ing-tpl-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Template name"
              className="h-8 w-44 text-sm"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}
            />
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="ing-tpl-category" className="h-8 w-40 text-sm">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`ing-tpl-cat-opt-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="button" size="sm" data-testid="ing-tpl-save" onClick={save} className="h-8 gap-1 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white">
              <Save className="w-3.5 h-3.5" /> Save
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setNaming(false); setName(""); setCategory("General"); }} className="h-8">Cancel</Button>
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="ing-tpl-add"
            onClick={() => setNaming(true)}
            className="h-8 gap-1 border-eucalyptus-700 text-eucalyptus-900"
          >
            <BookmarkPlus className="w-3.5 h-3.5" /> Save as template
          </Button>
        )}
      </div>
      {templates.length > 0 && (
        <div className="space-y-2" data-testid="ing-tpl-list">
          {TEMPLATE_CATEGORIES
            .map((cat) => ({ cat, rows: templates.filter((t) => (t.category || "General") === cat) }))
            .filter((g) => g.rows.length > 0)
            .concat(
              // any templates whose category isn't in the known list
              (() => {
                const known = new Set(TEMPLATE_CATEGORIES);
                const others = templates.filter((t) => !known.has(t.category || "General"));
                return others.length ? [{ cat: "Other", rows: others }] : [];
              })(),
            )
            .map(({ cat, rows }) => (
              <div key={cat} data-testid={`ing-tpl-group-${cat}`}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-eucalyptus-700/80 mb-1">{cat}</div>
                <div className="flex flex-wrap gap-1.5">
                  {rows.map((t) => (
                    <span key={t.id} className="inline-flex items-center gap-1 rounded-full bg-eucalyptus-100 pl-3 pr-1 py-1 text-xs">
                      <button
                        type="button"
                        data-testid={`ing-tpl-apply-${t.id}`}
                        onClick={() => onApply?.(t.text)}
                        className="font-medium text-eucalyptus-900 hover:underline"
                        title="Insert this ingredient list"
                      >
                        {t.name}
                      </button>
                      <button
                        type="button"
                        data-testid={`ing-tpl-del-${t.id}`}
                        onClick={() => remove(t.id)}
                        className="p-0.5 text-eucalyptus-700 hover:text-red-600"
                        aria-label="Delete template"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
